import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { GlobalConstants } from '../global-constants';

@Injectable({
  providedIn: 'root'
})
export class FuncionesService {
  userData: any;

  constructor(@Inject(DOCUMENT) private doc: Document,
              private router: Router,
              private titleService: Title, private metaService: Meta) { }

  validateEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  quitarAcentos(cadena: string): string {
    return cadena.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  generateSlug(text: string): string {
    return this.quitarAcentos(text)
      .toLowerCase() // Convertir a minúsculas
      .trim() // Eliminar espacios en los extremos
      .replace(/[^a-z0-9\s-]/g, '') // Eliminar caracteres especiales
      .replace(/\s+/g, '-') // Reemplazar espacios por guiones
      .replace(/-+/g, '-'); // Eliminar guiones duplicados
  }

  replaceSpacesWithPlus(text: string): string {
    if (text != undefined)
      return text.replace(/ /g, '+');
    else
      return '';
  }

  stringToDate(dateString:string) {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' }); // Ejemplo: "11/12/2024"
  }

  calculateDateDifference(date1: Date, date2: Date): { 
    days: number, 
    hours: number, 
    minutes: number, 
    seconds: number 
  } {
    // Obtén la diferencia en milisegundos
    const diffInMs = date2.getTime() - date1.getTime(); // No usamos Math.abs()

    // Determina si la fecha está en el pasado
    const isPast = diffInMs < 0;

    // Convierte a valores absolutos para calcular
    const absDiff = Math.abs(diffInMs);

    // Calcula días, horas, minutos y segundos
    const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((absDiff % (1000 * 60)) / 1000);

    // Si la fecha está en el pasado, devuelve valores negativos
    return {
      days: isPast ? -days : days,
      hours: isPast ? -hours : hours,
      minutes: isPast ? -minutes : minutes,
      seconds: isPast ? -seconds : seconds,
    };
  }

  convertirDiasEnMesesYDias(dias: number): { meses: number, diasRestantes: number } {
    const meses = Math.floor(dias / 30);
    const diasRestantes = dias % 30;
    return { meses, diasRestantes };
  }

  calificacionEstrellas(stars:number) {
    if (stars >= 4.5 && stars <= 5) {
      return "Excelente";
    } else if (stars >= 3.5 && stars < 4.5) {
      return "Muy bueno";
    } else if (stars >= 2.5 && stars < 3.5) {
      return "Aceptable";
    } else if (stars >= 1.5 && stars < 2.5) {
      return "Deficiente";
    } else if (stars >= 1 && stars < 1.5) {
      return "Muy malo";
    } else {
      return "";
    }
  }

  validarContrasena(contrasena: string): boolean {    
    // Expresión regular para cumplir con los requisitos:
    // - Entre 8 y 48 caracteres.
    // - Al menos una letra minúscula.
    // - Al menos una letra mayúscula.
    // - Al menos un número.
    // - No puede contener espacios.
    if (contrasena != '' && contrasena != undefined) {
      const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[^\s]{8,48}$/;

      return regex.test(contrasena);
    } else {
      return true;
    }
  }

  obtenerLongitudTexto(html: string): number {
    const textoPlano = html.replace(/<[^>]*>/g, ""); // Elimina etiquetas HTML
    return textoPlano.length;
  }

  esUrlValida(url: string): boolean {
    if (url != '' && url != undefined && url != null) {
      const regex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      return regex.test(url);
    } else {
      return true;
    }
  }

  convertirAPlano(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.body.textContent || "";
  }

  truncateText(text: string, maxLength: number): string {
    return text.length > maxLength ? text.substring(0, maxLength) : text;
  }

  setCanonicalURL(url: string = '') {
    if (url == '') {
      url = GlobalConstants.urlWeb.slice(0, -1) + this.router.url;
    }
    const existingLink = this.doc.querySelector("link[rel='canonical']");
    
    if (existingLink instanceof HTMLLinkElement) {
      existingLink.href = url;
    } else {
      const link: HTMLLinkElement = this.doc.createElement('link');
      link.rel = 'canonical';
      link.href = url;
      this.doc.head.appendChild(link);
    }
  }

  saludarSegunHora() {
    const horaActual = new Date().getHours();

    if (horaActual >= 5 && horaActual < 12) {
      return 'Buenos días';
    } else if (horaActual >= 12 && horaActual < 18) {
      return 'Buenas tardes';
    } else {
      return 'Buenas noches';
    }
  }

  /**
   * Recorta una imagen al cuadrado y la redimensiona.
   * @param archivo File o Blob de la imagen
   * @param tamaño Lado del cuadrado de salida (px)
   * @returns Promise<string> con DataURL de la imagen resultante
   */
  recortarImagenCuadrada(
    archivo: File | Blob,
    tamaño: number
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      // Crear un objeto URL temporal
      const url = URL.createObjectURL(archivo);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = tamaño;
        canvas.height = tamaño;

        const ctx = canvas.getContext('2d');
        if (!ctx) return reject('No se pudo obtener el contexto de canvas');

        // Determinar el lado más corto y coordenadas centradas
        const ladoCorto = Math.min(img.width, img.height);
        const x = (img.width - ladoCorto) / 2;
        const y = (img.height - ladoCorto) / 2;

        // Limpiar canvas
        ctx.clearRect(0, 0, tamaño, tamaño);

        // Dibujar imagen recortada y escalada
        ctx.drawImage(
          img,
          x, y, ladoCorto, ladoCorto,
          0, 0, tamaño, tamaño
        );

        // Generar DataURL final
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

        // Liberar URL temporal
        URL.revokeObjectURL(url);

        resolve(dataUrl);
      };

      img.onerror = (err) => {
        URL.revokeObjectURL(url);
        reject(err);
      };

      // Asignar fuente
      img.src = url;
    });
  }

  formatDateToUserTZ(dateUtc: string): string {
    const date = new Date(dateUtc + 'Z');

    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }

  unirConY(...palabras: string[]): string {
    if (palabras.length === 0) return '';
    if (palabras.length === 1) return palabras[0];
    if (palabras.length === 2) return `${palabras[0]} y ${palabras[1]}`;

    const ultimo = palabras.pop();
    return `${palabras.join(', ')} y ${ultimo}`;
  }  

  goPerfil(slug: string) {
    window.open(GlobalConstants.urlServer + 'perfil/' + slug, '_blank', 'noopener,noreferrer');
  }

  isValidCoordinate(lat: number, lon: number): boolean {
    return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
  }

  /**
   * Convierte grados a radianes
   */
  deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  esDistintaFecha(fecha1: string, fecha2: string): boolean {

    const f1 = new Date(fecha1);
    const f2 = new Date(fecha2);

    f1.setHours(0,0,0,0);
    f2.setHours(0,0,0,0);

    return f1.getTime() !== f2.getTime();
  }  

  irAlMarcador(idDelElemento: string) {
    const elemento = document.getElementById(idDelElemento);
    
    if (elemento) {
      // 1. Definimos cuántos píxeles queremos dejar libres arriba (ej. altura de tu menú)
      const offset = 100; 
      
      // 2. Calculamos la posición exacta del elemento en la página
      const posicionElemento = elemento.getBoundingClientRect().top + window.pageYOffset;
      
      // 3. Le restamos el margen
      const posicionFinal = posicionElemento - offset;

      // 4. Hacemos el scroll suave hasta esa posición exacta
      window.scrollTo({
        top: posicionFinal,
        behavior: 'smooth'
      });
    }
  }

}
